import { FC, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Input, Modal, Row } from 'antd';
import { AxiosResponse } from 'axios';
import { TbArrowRight, TbSquareRoundedFilled } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { PlatformForm as Form, PlatformColorPicker } from '@/shared/components/forms';
import { SetStateMethod } from '@/types';

interface RenameStageProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    columns: any[];
    setColumns: SetStateMethod<any[]>;
    currentColumn: any;
}

const RenameStage: FC<RenameStageProps> = ({ open, setOpen, setColumns, currentColumn }): JSX.Element => {
    const [loading, setLoading] = useState(false);

    const [color, setColor] = useState(currentColumn?.color ? currentColumn?.color : '');
    const [name, setName] = useState(currentColumn?.name ? currentColumn?.name : '');

    const handleColumnPropertiesUpdate = async () => {
        if (currentColumn?.id) {
            try {
                setLoading(true);

                const res: AxiosResponse = await authorizedAxiosInstance.put(
                    `/api/pipelines/columns/${currentColumn?.id}`,
                    { color, name },
                );

                if (res?.status === 200) {
                    setColumns((prev: any) =>
                        prev.map((col: any) =>
                            col.name === currentColumn.name
                                ? { ...col, name: res?.data?.name, color: res?.data?.color }
                                : col,
                        ),
                    );
                    setColor('');
                    setName('');
                    setOpen(false);
                }
            } catch (error) {
                captureException(error);
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <Modal
            afterOpenChange={(nextOpen) => {
                if (nextOpen) {
                    setColor(currentColumn?.color ?? '');
                    setName(currentColumn?.name ?? '');
                    setLoading(false);
                }
            }}
            open={open}
            mask={false}
            title='Rename Stage'
            bodyStyle={{ paddingTop: '0.5rem' }}
            okText={
                <>
                    Save <TbArrowRight style={{ verticalAlign: '-0.15rem' }} />
                </>
            }
            okButtonProps={{
                disabled: (currentColumn?.color === color && currentColumn?.name === name) || loading,
                loading: loading,
            }}
            onOk={handleColumnPropertiesUpdate}
            onCancel={() => setOpen(false)}
        >
            <Form layout='vertical'>
                <Row gutter={[24, 0]}>
                    <Col span={2}>
                        <Form.Item label=' ' style={{ marginBottom: 0 }}>
                            <PlatformColorPicker
                                onChange={(color) => {
                                    setColor(color.toHexString());
                                }}
                            >
                                <Button
                                    icon={
                                        <TbSquareRoundedFilled
                                            size='1rem'
                                            style={{ verticalAlign: '-0.125rem', color: color }}
                                        />
                                    }
                                />
                            </PlatformColorPicker>
                        </Form.Item>
                    </Col>
                    <Col span={22}>
                        <Form.Item style={{ marginBottom: 0 }} label='Stage Name'>
                            <Input
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default RenameStage;
